import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useReactToPrint } from 'react-to-print';
import { IoPrintOutline } from 'react-icons/io5';
import { MdOutlinePerson, MdOutlinePersonOff } from 'react-icons/md';
import GaugeComponent from 'react-gauge-component';
import moment from 'moment';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import Spinner from 'assets/gif/loading.gif';
import axios from 'config/axios';
import Header from 'components/Header';
import Button from 'components/Button';
import ButtonLink from 'components/ButtonLink';
import LevelBar from 'components/LevelBar';
import Table from 'components/Table';
import Dropdown from 'components/Dropdown2';
import Loader from 'components/Loader';
import ScoreWithScale from 'components/ScoreWithScale2';
import useWindowSize from 'hooks/useWindowSize';

import './index.css';
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import Dropdown4 from 'components/Dropdown4';
import QuestionPopup from 'components/QuestionPopup';

const MULTIPLE_CHOICES = ['A', 'B', 'C', 'D', 'E'];
const CONFIGURATION= {
    'Reading and Writing': 'RW',
    'Math': 'M'
}

function ClassPaperReport () {
    const [isQuestionPopupOpen, setIsQuestionPopupOpen] = useState(false);
    const [questionId, setQuestionId] = useState(null);
    const [correctStudent, setCorrectStudent] = useState(0);
    const [currentQuestionData, setCurrentQuestionData] = useState(null);
    const [testType, setTestType] = useState('');
    const [scoreChartData, setScoreChartData] = useState<{ range: string; students: number }[]>([]);
    const [testData, setTestData] = useState(null);
    const [modules, setModules] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [moduleIndex, setModuleIndex] = useState(0);
    const [subjectIndex, setSubjectIndex] = useState(0);
    const [downloadInProgress, setDownloadInProgress] = useState(false);
    const [printInProgress, setPrintInProgress] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [hideStudentNames, setHideStudentNames] = useState(false);

    const windowSize = useWindowSize();
    const { classId, testId } = useParams();
    const printRef = useRef(null);
    const questionPopupRef = useOutsideClickHandler(() => handleExit(false));
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const pathParts = location.pathname.split('/');
        const testType = pathParts[2]; // ex: 'digital-act' or 'digital-sat'
        setTestType(testType);
    }, [location]);

    const exportStudentTestData = () => {
        setDownloadInProgress(true);
        axios({
            url: `tutors/report/download/${classId}/${testId}/`,
            method: 'GET',
            responseType: 'blob',
        })
        .then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${testData?.className}-${testData?.testName}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setDownloadInProgress(false);
        })
        .catch(error => {
            console.error('Error downloading CSV:', error);
            setDownloadInProgress(false);
        });
    }

    const printBatchReport = () => {
        setPrintInProgress(true);
        axios.get(`tutors/report/print/${classId}/${testId}`)
            .then(res => {
                setReportData(res.data.studentTests);
                setPrintInProgress(false);
            })
            .catch(error => {
                console.log(error);
                setPrintInProgress(false);
            });

    }

    const handleQuestionPopup = (questionId: string) => {
        setQuestionId(questionId);
        axios.get(`tutors/classes/report/question/${questionId}/${testId}/`)
            .then(res => {
                setCurrentQuestionData(res.data)
            })
            .catch(error => {
                console.log(error);
            });

        setIsQuestionPopupOpen(true);
    }

    const formatTime = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = (hours && duration.minutes() < 10) ? `0${duration.minutes()}` : duration.minutes();
        const seconds = duration.seconds() < 10 ? `0${duration.seconds()}` : duration.seconds();

        return hours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
    }

    const formatTime2 = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = duration.minutes();
        const seconds = duration.seconds();

        return hours 
            ? `${hours} hr ${minutes} min ${seconds} sec`
            : minutes
            ? `${minutes} min ${seconds} sec`
            : `${seconds} sec`;
    }

    const handlePrint = useReactToPrint({
        content: () => printRef.current
    });

    const handleExit = (isExit: boolean) => {
        setIsQuestionPopupOpen(isExit);
        setCurrentQuestionData(null);
    }

    useEffect(()=>{
        if (testData && questionId) {
            let correctCount = 0;
            let taken = 0;

            testData.students.forEach(student => {
                student.moduleAnswers.forEach(moduleAnswer => {
                    const answer = moduleAnswer.answers.find(a => a.questionId === questionId);
                    if (answer) {
                        taken +=1;
                        if (answer.isCorrect) {
                            correctCount += 1;
                        }
                    }
                });
            });

            setCorrectStudent(taken>0? (correctCount/taken)*100: null);
        }
    }, [testData, questionId]);

    useEffect(() => {
        if (!testType) return;

        axios.get(`tutors/classes/${classId}/${testType}/report/${testId}/`)
            .then(res => {
                setTestData(res.data);
                let modulesArray = [];
                let subjectsArray = [];
                for (let module of res.data.modules) {
                    modulesArray.push(module.sectionName)
                }
                setModules(modulesArray);
                if (!res.data.isConfigured) {
                    subjectsArray.push('Total')
                }
                for (let subject of res.data.configTestSections) {
                    subjectsArray.push(subject)
                }
                setSubjects(subjectsArray);
            })
            .catch(error => {
                console.log(error);
            });
    }, [testType]);

    useEffect(() => {
        if (reportData) {
            handlePrint();
        }
    }, [reportData]);

    if (testData === null) return <Loader />;

    return (
        <div className='classPaperACTReport'>
            <Helmet>
                <title>
                    Class Report - Test Center Admin
                </title>
            </Helmet>
            {isQuestionPopupOpen &&
                <QuestionPopup
                    info={currentQuestionData?.info}
                    correctStudent={correctStudent!==null? `${correctStudent.toFixed(1)}%`: '-'}
                    testName={testData?.testName}
                    question={currentQuestionData?.question}
                    passages={currentQuestionData?.passages}
                    figures={currentQuestionData?.figures}
                    choices={currentQuestionData?.choices}
                    correctChoice={currentQuestionData?.correctChoice}
                    correctAnswers={currentQuestionData?.correctAnswers}
                    manageExit={() => handleExit(false)}
                    innerRef={questionPopupRef}
                />
            }
            <Header
                title={(testData?.testName && testData?.configTestSections?.length>0)? (testData?.configTestSections?.length==1?`${testData.testName} (${testData.configTestSections.map(section=> CONFIGURATION[section]).join(', ')})`:`${testData.testName}`) : null}
                prevRoutes={testData && [{ name: testData.className, path: `/classes/${classId}` }]}
            />
            <div className='classPaperACTReport__subHeaderRow'>
                <h2 className='classPaperACTReport__subHeader'>
                    Scores by students
                </h2>
                <div className='classPaperACTReport__buttonGroup'>
                    <Button className='classPaperACTReport__button' secondary onClick={() => setHideStudentNames(!hideStudentNames)}>
                        {hideStudentNames
                        ?   <>
                                <MdOutlinePerson className='classPaperACTReport__buttonIcon' />
                                <p>Show</p>
                            </>
                        :   <>
                                <MdOutlinePersonOff className='classPaperACTReport__buttonIcon' />
                                <p>Hide</p>
                            </>
                        }
                    </Button>
                    {/* <Button className='classPaperACTReport__button' onClick={printBatchReport}>
                        {printInProgress
                        ?   <img src={Spinner} />
                        :   <>
                                <IoPrintOutline className='classPaperACTReport__buttonIcon' />
                                <p>Report</p>                            
                            </>
                        }
                    </Button> */}
                </div>
            </div>
            <div className='classPaperACTReport__scoresContainer'>
                <Table
                    className='classPaperACTReport__table'
                    header={[
                        'Students',
                        ...testData.classTestSections.map((classTestSection, p) => (
                            <p className='classPaperACTReport__cellCenter'>
                                {classTestSection}
                            </p>
                        )),
                        <p className='classPaperACTReport__cellCenter'>Score</p>,
                        <p className='classPaperACTReport__cellCenter'>Progress</p>
                    ]}
                    columnTypes={[
                        'string',
                        ...Array(testData.classTestSections.length).fill('number'),
                        'number',
                        null
                      ]}
                    rows={testData.students.map(student => {
                        // const firstScoreIndex = student.testScores.findIndex(score => score !== null);
                        // const lastScoreIndex = student.testScores.findLastIndex(score => score !== null);
                        // const progress = 100*(student.testScores[lastScoreIndex]-student.testScores[firstScoreIndex])/student.testScores[firstScoreIndex]

                        return [
                            hideStudentNames ? null : `${student.firstName} ${student.lastName}`,
                            ...testData.classTestSections.map((subjectName) => (
                                (() => {
                                    const testSection = student.test?.testSections.find(
                                    (section) => section.name === subjectName
                                    );
                                    return testSection?.score ? Math.floor(testSection.score) : null;
                                })()
                            )),
                            student.test.score ? Math.floor(student.test.score) : null,
                            <div className='classPaperACTReport__progress classPaperACTReport__cellCenter'>
                                {new Array(student.test.testModuleCount).fill(null).map((_count, c) => (
                                    <div className={student.test.completedModuleCount > c
                                        ? 'classPaperACTReport__progressBar--filled'
                                        : (student.test.inProgress && c === 0)
                                        ? 'classPaperACTReport__progressBar--started'
                                        : 'classPaperACTReport__progressBar'
                                    }></div>
                                ))}
                            </div>
                        ];
                    })}
                    onSelectCell={testData.students.map(student => ([
                        hideStudentNames ? null : () => navigate(`/students/${student.studentId}`),
                        ...testData.classTestSections.map(() => null),
                        student.test.score? () => navigate(`/report/paper/${student.test.testId}`): null,
                        null
                    ]))}
                    navigator={false}
                />
                {/* <div className='classPaperACTReport__category'>
                    <div className='classPaperACTReport__scoreDistribution__title'>Category breakdown (%correct)</div>
                    <div className='classPaperACTReport__scoreDistribution__analysisWrap'>
                        {Object.keys(testData.avgCorrectPercent).length > 0 ? (
                            Object.entries(testData.avgCorrectPercent).map(([subject, categories], index) => (
                                <div key={index} className='classPaperACTReport__analysisBox'>
                                    <div className='classPaperACTReport__analysisBoxTitle'>{subject}</div>
                                    <div className='classPaperACTReport__analysisContentWrap'>
                                        {Object.entries(categories)?.map(([categoryName, answerNumInfo], i) => {
                                            const { numCorrect, numQuestions } = answerNumInfo;
                                            const percent = numQuestions > 0 ? (numCorrect / numQuestions) * 100 : 0;
                                            return (
                                                <div key={i} className='classPaperACTReport__categoryWrap'>
                                                    <div className='classPaperACTReport__categoryTitle'>{categoryName}</div>
                                                    <LevelBar className={subject==='Reading and Writing'
                                                                        ? 'LevelBar__greenBox'
                                                                        : 'LevelBar__orangeBox'
                                                                        } 
                                                                percent = {percent}/>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))
                        ): (
                            <div className='classPaperACTReport__analysisBox'>
                                <div className='classPaperACTReport__analysisBoxTitle'>-</div>
                                <div className='classPaperACTReport__analysisContentWrap'>
                                <div className='classPaperACTReport__categoryWrap'>
                                    <div className='classPaperACTReport__categoryTitle'>-</div>
                                    <LevelBar className='LevelBar__defaultBox' percent={0} />
                                </div>
                                </div>
                            </div>
                            )
                        }
                    </div>
                </div> */}
            </div>
            <div className='classPaperACTReport__subHeaderRow'>
                <h2 className='classPaperACTReport__subHeader'>
                    Student responses
                </h2>
                <Dropdown
                    className='classPaperACTReport__dropdown'
                    data={modules}
                    onSelect={value => setModuleIndex(modules.findIndex(module => module === value))}
                    value={modules[moduleIndex]}
                />
                <div className='classPaperACTReport__buttonGroup'>
                    {/* <Button className='classPaperACTReport__button' onClick={printBatchReport}>
                        {printInProgress ? <img src={Spinner} /> : 'Print'}
                    </Button> */}
                    {/* <Button className='classPaperACTReport__button' onClick={exportStudentTestData}>
                        {downloadInProgress ? <img src={Spinner} /> : 'Download'}
                    </Button> */}
                </div>
            </div>
            <Table
                header={['Students', ...testData.modules[moduleIndex].questions.map((question, q) => (
                    // <p className={`
                    //     classPaperACTReport__cellCenter
                    //     classPaperACTReport__response 
                    //     classPaperACTReport__cursor
                    //     ${testData.students.filter(student => (
                    //         student.moduleAnswers.find(moduleAnswer => (
                    //             moduleAnswer.subject === testData.modules[moduleIndex].subject
                    //             && moduleAnswer.moduleNumber === testData.modules[moduleIndex].moduleNumber
                    //             && moduleAnswer.difficulty === testData.modules[moduleIndex].difficulty
                    //         ))
                    //     )).find(student => (
                    //         !student.moduleAnswers.find(moduleAnswer => (
                    //             moduleAnswer.subject === testData.modules[moduleIndex].subject
                    //             && moduleAnswer.moduleNumber === testData.modules[moduleIndex].moduleNumber
                    //             && moduleAnswer.difficulty === testData.modules[moduleIndex].difficulty
                    //         )).answers[q].isCorrect
                    //     )) && 'classPaperACTReport__cellWrong'}
                    // `}
                    <p className={`
                        classPaperACTReport__cellCenter
                        classPaperACTReport__response 

                    `}
                    onClick={() => {}}>
                        Q{question[0]}
                    </p>
                ))]}
                rows={[
                    [
                        <p className='classPaperACTReport__header'># Incorrect</p>,
                        ...testData.modules[moduleIndex].questions.map((_question, q) => (
                            <p className='classPaperACTReport__header classPaperACTReport__cellCenter'>
                                {testData.students.filter(student => (
                                    student.moduleAnswers.find(moduleAnswer => (
                                        // moduleAnswer.subject === testData.modules[moduleIndex].subject
                                        // && moduleAnswer.moduleNumber === testData.modules[moduleIndex].moduleNumber
                                        // && moduleAnswer.difficulty === testData.modules[moduleIndex].difficulty
                                        moduleAnswer.sectionName === testData.modules[moduleIndex].sectionName
                                    ))
                                )).filter(student => (
                                    !student.moduleAnswers.find(moduleAnswer => (
                                        // moduleAnswer.subject === testData.modules[moduleIndex].subject
                                        // && moduleAnswer.moduleNumber === testData.modules[moduleIndex].moduleNumber
                                        // && moduleAnswer.difficulty === testData.modules[moduleIndex].difficulty
                                        moduleAnswer.sectionName === testData.modules[moduleIndex].sectionName
                                    )).answers[q].isCorrect
                                )).length
                            }</p>
                        ))
                    ],
                    ...testData.students.filter(student => (
                        student.moduleAnswers.find(moduleAnswer => (
                            // moduleAnswer.subject === testData.modules[moduleIndex].subject
                            // && moduleAnswer.moduleNumber === testData.modules[moduleIndex].moduleNumber
                            // && moduleAnswer.difficulty === testData.modules[moduleIndex].difficulty
                            moduleAnswer.sectionName === testData.modules[moduleIndex].sectionName
                        ))
                    )).map(student => ([
                        hideStudentNames ? null : `${student.firstName} ${student.lastName}`,
                        ...student.moduleAnswers.find(moduleAnswer => (
                            // moduleAnswer.subject === testData.modules[moduleIndex].subject
                            // && moduleAnswer.moduleNumber === testData.modules[moduleIndex].moduleNumber
                            // && moduleAnswer.difficulty === testData.modules[moduleIndex].difficulty
                            moduleAnswer.sectionName === testData.modules[moduleIndex].sectionName
                        )).answers.map(answer => (
                            <p className={`classPaperACTReport__cellCenter classPaperACTReport__response ${!answer.isCorrect && (answer.answer
                                ? 'classPaperACTReport__cellWrong'
                                : 'classPaperACTReport__cellSkipped'
                            )}`}>
                                {answer.answer ? answer.answer : '-'}
                            </p>
                        ))
                    ])),
                ]}
                navigator={false}
            />
            {reportData &&
                <div className='classPaperACTReport__report' ref={printRef}>
                    {reportData.map(testData => (<>
                        <div className='classReportPrint__headerGroup'>
                            <div className='classReportPrint__headerSection'>
                                {testData.logo && 
                                    <img
                                        className='classReportPrint__headerLogo'
                                        alt='logo'
                                        src={testData.logo}
                                    />
                                }
                            </div>
                            <div className='classReportPrint__headerSection'>
                                <div className='classReportPrint__headerInfo'>
                                    <p>
                                        Name: {testData.studentName}
                                    </p>
                                    <p>
                                        Email: {testData.email}
                                    </p>
                                    <p>
                                        Test name: {testData.name}
                                    </p>
                                    <p>
                                        Tested on: {moment(testData.testDate).format('MMMM D YYYY')}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className='classReportPrint__questionsOverview'>
                            {testData.testSections.map(testSection => <>
                                <p className='classReportPrint__moduleName'>
                                    {testSection.name}
                                </p>
                                <div className='classReportPrint__questionsSummary'>
                                    <div className='classReportPrint__questionsSummaryItem'>
                                        <p className='classReportPrint__questionSummaryNumber'>
                                            {testData.testModules.filter(testModule => (
                                                testModule.sectionName === testSection.name
                                                // && testModule.moduleNumber === moduleIndex + 1
                                            )).reduce((total, testModule) => (
                                                total + testModule.answers.length
                                            ), 0)}
                                        </p>
                                        <p className='classReportPrint__questionSummaryText'>
                                            Total Questions
                                        </p>
                                    </div>
                                    <div className='classReportPrint__questionsSummaryItem'>
                                        <p className='classReportPrint__questionSummaryNumber'>
                                            {testData.testModules.filter(testModule => (
                                                testModule.sectionName === testSection.name
                                                // && testModule.moduleNumber === moduleIndex + 1
                                            )).reduce((total, testModule) => (
                                                total + testModule.answers.filter(answer => answer.isCorrect).length
                                            ), 0)}
                                        </p>
                                        <p className='classReportPrint__questionSummaryText'>
                                            Correct Answers
                                        </p>
                                    </div>
                                    <div className='classReportPrint__questionsSummaryItem'>
                                        <p className='classReportPrint__questionSummaryNumber'>
                                            {testData.testModules.filter(testModule => (
                                                testModule.sectionName === testSection.name
                                                // && testModule.moduleNumber === moduleIndex + 1
                                            )).reduce((total, testModule) => (
                                                total + testModule.answers.filter(answer => !answer.isCorrect).length
                                            ), 0)}
                                        </p>
                                        <p className='classReportPrint__questionSummaryText'>
                                            Incorrect Answers
                                        </p>
                                    </div>
                                </div>
                                <table className='classReportPrint__questionsTable'>
                                    <tbody>
                                        <tr>
                                            <th>Question</th>
                                            <th>Correct Answer</th>
                                            <th>Your Answer</th>
                                            {/* <th>Category</th>
                                            {user.customizations.find(customization => customization === 'Show Correct Rate') &&
                                                <th>Correct %</th>
                                            } */}
                                            <th>Time Spent</th>
                                        </tr>
                                        {testData.testModules.filter(testModule => (
                                            testModule.sectionName === testSection.name
                                        ))[moduleIndex].answers.map(answer => (
                                            <tr key={testData.testModules[moduleIndex].name + answer.questionNumber}>
                                                <td>{answer.questionNumber}</td>
                                                <td>{answer.correctChoice !== null
                                                    ?   MULTIPLE_CHOICES[answer.correctChoice]
                                                    :   answer.correctAnswers.toString().replaceAll(',', ', ')
                                                }</td>
                                                <td>{answer.answer !== null
                                                    ?   (answer.correctAnswers.includes(answer.answer)
                                                            ? <p>{answer.answer}; Correct</p>
                                                            : <p className='classReportPrint__questionIncorrect'>{answer.answer}; Incorrect</p>
                                                        )
                                                    :   answer.choice !== null
                                                    ?   (answer.choice === answer.correctChoice
                                                            ? <p>{MULTIPLE_CHOICES[answer.choice]}; Correct</p>
                                                            : <p className='classReportPrint__questionIncorrect'>{MULTIPLE_CHOICES[answer.choice]}; Incorrect</p>
                                                        )
                                                    :   <p className='classReportPrint__questionOmitted'>Omitted</p>
                                                }</td>
                                                {/* <td>{answer.category}</td>
                                                {user.customizations.find(customization => customization === 'Show Correct Rate') &&
                                                    (answer.correctRate ? <td>{parseFloat(answer.correctRate).toFixed(1)}%</td> : <></>)
                                                } */}
                                                <td>
                                                    <p className='classReportPrint__timeSpentGood'>
                                                        {formatTime(answer.timeSpent)}
                                                    </p>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>)}
                        </div>
                    </>))}
                </div>
            }
        </div>
    );
}

export default ClassPaperReport;